import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "../auth/AuthContext.jsx";
import { api } from "../api/client.js";
import { useLoading } from "../loading/LoadingContext.jsx";
import AppHeader from "../components/AppHeader.jsx";
import SegControl from "../components/SegControl.jsx";
import { NoteStrip } from "../components/InfoSquare.jsx";
import StatRow from "./spend/StatRow.jsx";
import { SkeletonStats } from "./spend/Skeleton.jsx";
import DailySpendChart from "./spend/DailySpendChart.jsx";
import WhatMoved from "./spend/WhatMoved.jsx";
import Breakdown from "./spend/Breakdown.jsx";
import {
  DEFAULT_FORECAST_BASIS,
  DISPLAY_TIMEZONE_LABEL,
  FORECAST_BASES,
  basisLabel,
  comparisonLabel,
  formatBillingDate,
  formatClock,
  isCurrentMonth,
  money,
  paceForecast,
  periodLabel,
  periodLabelLong,
  previousPeriod,
} from "./spend/derive.js";

const MOVER_COUNT = 6;
const QUICK_PERIODS = 4; // months offered as segments; older months live in the select beside them
const ARCHIVE_PERIODS = 12;

// The nightly refresh runs once a day. Anything older than a day and a half
// means it has missed at least one run, which is worth saying on the page
// rather than leaving a user to wonder why today looks quiet.
const NIGHTLY_STALE_HOURS = 36;

const BACKFILL_OPTIONS = [
  { value: 2, label: "Refresh (2 months, default)" },
  { value: 3, label: "Backfill 3 months" },
  { value: 6, label: "Backfill 6 months" },
  { value: 12, label: "Backfill 12 months" },
];

/** First day of `period` to the last day so far (today, or month end), as YYYY-MM-DD. */
function monthRange(period) {
  const [y, m] = period.split("-").map(Number);
  const last = new Date(Date.UTC(y, m, 0));
  const today = new Date();
  const end = last < today ? last : today;
  return [`${period}-01`, end.toISOString().slice(0, 10)];
}

// Services whose cost rounds to $0.00 are not shown: an unused service is not
// a line on the bill.
const hasCost = (s) => Math.abs(Number(s.amount) || 0) >= 0.005;

/** "What moved" rows from a tagged breakdown: every account x service, ranked by change. */
function taggedMovers(tagged) {
  const rows = [];
  for (const a of tagged?.accounts || []) {
    for (const s of a.services || []) {
      if (Math.abs(s.amount - s.previous_amount) >= 0.005) {
        rows.push({
          account_id: a.account_id,
          account_name: a.account_name,
          service: s.service,
          amount: s.amount,
          previous_amount: s.previous_amount,
        });
      }
    }
  }
  rows.sort((x, y) => Math.abs(y.amount - y.previous_amount) - Math.abs(x.amount - x.previous_amount));
  return rows.slice(0, MOVER_COUNT);
}

function currentPeriod() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

function recentPeriods(count) {
  const now = new Date();
  return Array.from({ length: count }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
}

/**
 * The Spend page — Overview and By Service consolidated into one screen.
 *
 * The question this page exists to answer is "are we on track versus last
 * month, and what jumped?". Previously that took three pages, two date
 * pickers and a good memory for last month's numbers. Here the period is
 * chosen once at the top and every section below answers against it: the
 * stat row for the shape of the month, the chart for which days, the mover
 * list for which service and account, and the breakdown for the resource
 * behind it.
 *
 * Only six things live in state; everything else — totals, deltas, the mover
 * ranking, bar geometry, anomaly flags — is derived per render in
 * spend/derive.js. Storing any of it would create a second source of truth
 * for numbers that must agree across four sections.
 */
export default function Spend() {
  const { idToken } = useAuth();
  const { start, stop } = useLoading();
  const [params, setParams] = useSearchParams();

  // period / account / service live in the URL so a drill can be pasted into
  // Slack — none of this was linkable before.
  const period = params.get("period") || currentPeriod();
  const accountId = params.get("account") || null;
  const service = params.get("service") || null;
  // The averaging window behind the pace forecast. In the URL with the rest so
  // a shared link carries the same projection the sender was looking at — two
  // people reading different forecasts off the same link would be worse than
  // having no control at all.
  const basis = FORECAST_BASES.some((b) => b.value === params.get("basis"))
    ? params.get("basis")
    : DEFAULT_FORECAST_BASIS;

  // Tag filter ("Project" = "P2M") lives in the URL with the rest, so a
  // filtered drill-down can be shared as a link.
  const tagKey = params.get("tag") || null;
  const tagValue = params.get("tagv") || null;
  const tagFilter = useMemo(() => (tagKey && tagValue ? { key: tagKey, values: [tagValue] } : null), [tagKey, tagValue]);
  const tagId = tagFilter ? `${tagKey}=${tagValue}` : "";

  const [compare, setCompare] = useState(true);
  const [hover, setHover] = useState(null);
  const [note, setNote] = useState(null);

  const [accounts, setAccounts] = useState([]);
  const [daily, setDaily] = useState(null);
  const [movers, setMovers] = useState([]);
  // What each compare=previous response was measured against — the same days
  // of last month while this one is in progress, else the whole month.
  const [moversComparison, setMoversComparison] = useState(null);
  const [accountsComparison, setAccountsComparison] = useState(null);
  const [untracked, setUntracked] = useState([]);
  const [savings, setSavings] = useState(null);
  // Tracked separately from the global loading bar: the bar says "something is
  // in flight", these say WHICH pane has nothing to show yet, which is what
  // stops an empty state being rendered as though it were a result.
  const [pageLoading, setPageLoading] = useState(true);
  const [servicesLoading, setServicesLoading] = useState(false);
  const [services, setServices] = useState([]);
  const [usageTypes, setUsageTypes] = useState([]);
  const [usageLoading, setUsageLoading] = useState(false);
  const [usageError, setUsageError] = useState(null);
  const [cacheInfo, setCacheInfo] = useState(null);
  // Nightly-snapshot timestamps, kept apart from `accounts` because in tag
  // mode the account rows come from Cost Explorer and carry none.
  const [snapshotStamps, setSnapshotStamps] = useState([]);
  const [tagged, setTagged] = useState(null);
  // null = not loaded, "loading", or { keys, inactive, noData }
  const [tagKeys, setTagKeys] = useState(null);
  const [tagValues, setTagValues] = useState([]);

  const [errorMsg, setErrorMsg] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshMonths, setRefreshMonths] = useState(2);
  const [menuOpen, setMenuOpen] = useState(false);
  const [exportingPayer, setExportingPayer] = useState(null);

  const prevPeriod = previousPeriod(period);

  const setParam = useCallback(
    (patch) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          Object.entries(patch).forEach(([k, v]) => {
            if (v === null || v === undefined) next.delete(k);
            else next.set(k, v);
          });
          return next;
        },
        { replace: true }
      );
    },
    [setParams]
  );

  // --- page-level data: accounts, dailies, movers ------------------------
  // `force` is passed through to the endpoints that keep their OWN cache.
  // Without it, "Refresh from AWS" re-pulled Cost Explorer into the snapshot
  // table and then re-read a six-hour-old daily series on top of it — so the
  // chart could sit on yesterday's day count with nothing on the page able to
  // shift it. The account totals have no such cache, hence only the daily
  // series takes the flag.
  const loadPage = useCallback((force = false) => {
    setErrorMsg(null);
    setHover(null);
    setPageLoading(true);
    start();
    Promise.all([
      api.getCostByAccount(idToken, period, "previous"),
      api.getCostDaily(idToken, period, undefined, force, tagFilter).catch((err) => {
        // The daily series is the one genuinely new endpoint; if it is not
        // deployed yet the rest of the page is still useful, so this degrades
        // to "no chart" rather than taking the whole page down.
        console.warn("daily series unavailable:", err.message);
        return null;
      }),
      // With a tag filter the movers come from the tagged breakdown instead —
      // the snapshot the movers endpoint reads has no tag dimension.
      tagFilter
        ? null
        : api.getCostMovers(idToken, period, MOVER_COUNT).catch((err) => {
            console.warn("movers unavailable:", err.message);
            return null;
          }),
      // Summary only — the strip is a prompt to go and look, so a failure here
      // must never cost the page its actual content.
      api.getSavings(idToken, period).catch((err) => {
        console.warn("savings unavailable:", err.message);
        return null;
      }),
      tagFilter ? api.getCostTagged(idToken, period, tagFilter, force) : null,
    ])
      .then(([acc, day, mov, sav, tg]) => {
        setSnapshotStamps((acc.accounts || []).map((a) => a.last_updated).filter(Boolean));
        // Tag mode: accounts, services and movers all come from the tagged
        // breakdown. A budget is for an account's whole bill; against a
        // tagged slice it would read as "well under budget" when it is not.
        const list = tg ? (tg.accounts || []).map((a) => ({ ...a, monthly_budget: null })) : acc.accounts || [];
        setTagged(tg);
        setAccounts(list);
        setUntracked(tg ? [] : acc.untracked || []);
        setDaily(day);
        setMovers(tg ? taggedMovers(tg) : mov?.movers || []);
        setMoversComparison(tg ? tg.comparison || null : mov?.comparison || null);
        setAccountsComparison(tg ? tg.comparison || null : acc.comparison || null);
        setSavings(sav);
        if (list.length && !list.some((a) => a.account_id === accountId)) {
          setParam({ account: list[0].account_id, service: null });
        }
      })
      .catch((err) => setErrorMsg(err.message))
      .finally(() => {
        setPageLoading(false);
        stop();
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idToken, period, tagId]);

  useEffect(() => {
    loadPage(false);
  }, [loadPage]);

  // --- tag filter options -------------------------------------------------
  // Keys are fetched the first time the filter is opened (or straight away
  // when a link arrives with one set): a GetTags call on every page view
  // would be a billed request most visitors never use.
  const ensureTagKeys = useCallback(() => {
    if (tagKeys !== null) return;
    const [from, to] = monthRange(period);
    setTagKeys("loading");
    api
      .getCostTagsAll(idToken, from, to)
      .then((r) => setTagKeys({ keys: r.keys || [], inactive: r.inactive_keys || [], noData: r.active_without_data || [] }))
      .catch((err) => {
        setTagKeys(null);
        setErrorMsg(err.message);
      });
  }, [idToken, period, tagKeys]);

  useEffect(() => {
    setTagKeys(null);
  }, [period]);

  useEffect(() => {
    if (!tagKey) {
      setTagValues([]);
      return;
    }
    const [from, to] = monthRange(period);
    api
      .getCostTagsAll(idToken, from, to, tagKey)
      .then((r) => setTagValues(r.values || []))
      .catch((err) => setErrorMsg(err.message));
  }, [idToken, period, tagKey]);

  // --- pane 2: services for the selected account -------------------------
  useEffect(() => {
    if (!accountId) return;
    if (tagFilter) {
      // Already in the tagged breakdown — no second request.
      if (!tagged) return;
      const list = ((tagged.accounts || []).find((a) => a.account_id === accountId)?.services || []).filter(hasCost);
      setServices(list);
      if (list.length && !list.some((s) => s.service === service)) setParam({ service: list[0].service });
      return;
    }
    setServicesLoading(true);
    start();
    api
      .getCostByService(idToken, accountId, { period }, false, null, "previous")
      .then((r) => {
        const list = r.services || [];
        setServices(list);
        // Selecting an account selects its largest service, so pane 3 is never
        // left empty waiting for a second click.
        if (list.length && !list.some((s) => s.service === service)) {
          setParam({ service: list[0].service });
        }
      })
      .catch((err) => {
        if (err.message.includes("404")) setServices([]);
        else setErrorMsg(err.message);
      })
      .finally(() => {
        setServicesLoading(false);
        stop();
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idToken, accountId, period, tagId, tagged]);

  // --- pane 3: usage types for the selected account + service ------------
  const loadUsageTypes = useCallback(
    (force) => {
      if (!accountId || !service) {
        setUsageTypes([]);
        return;
      }
      setUsageLoading(true);
      setUsageError(null);
      start();
      api
        .getCostUsageTypes(idToken, accountId, service, { period }, force, tagFilter, "previous")
        .then((r) => {
          setUsageTypes(r.usage_types || []);
          setCacheInfo({ cached: r.cached, cachedAt: r.cached_at });
        })
        .catch((err) => {
          setUsageTypes([]);
          setUsageError(err.message);
        })
        .finally(() => {
          setUsageLoading(false);
          stop();
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [idToken, accountId, service, period, tagId]
  );

  useEffect(() => {
    loadUsageTypes(false);
  }, [loadUsageTypes]);

  const onRefresh = async () => {
    setRefreshing(true);
    setMenuOpen(false);
    start();
    try {
      await api.refresh(idToken, refreshMonths > 2 ? refreshMonths : undefined);
      // force: an explicit refresh must bypass every cache, not just re-pull
      // the snapshot table and then read a stale daily series over the top.
      loadPage(true);
    } catch (err) {
      setErrorMsg(err.message);
    } finally {
      setRefreshing(false);
      stop();
    }
  };

  const onExport = useCallback(
    async (payerAccountId, payerName) => {
      setExportingPayer(payerAccountId);
      setErrorMsg(null);
      start();
      try {
        const csv = await api.exportCosts(idToken, period, payerAccountId);
        // A Blob + object URL rather than navigating to the endpoint: the
        // request needs the Cognito Authorization header, which a plain link
        // cannot carry.
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `aws-billing_${(payerName || payerAccountId)
          .replace(/[^A-Za-z0-9]+/g, "-")
          .replace(/^-|-$/g, "")
          .toLowerCase()}_${period}.csv`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
      } catch (err) {
        setErrorMsg(err.message);
      } finally {
        setExportingPayer(null);
        stop();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [idToken, period]
  );

  // --- freshness stamp ---------------------------------------------------
  const nightlyOk = useMemo(() => {
    if (!snapshotStamps.length) return null;
    const newest = Math.max(...snapshotStamps.map((s) => new Date(s).getTime()));
    return Date.now() - newest < NIGHTLY_STALE_HOURS * 60 * 60 * 1000;
  }, [snapshotStamps]);

  // The date is Cost Explorer's UTC billing day; the times beside it are
  // Manila wall-clock. See formatBillingDate in derive.js for why the two are
  // deliberately not in the same zone.
  const dataThrough = formatBillingDate(daily?.data_through);
  const dataThroughTime = formatClock(daily?.data_through);
  const cachedTime = formatClock(daily?.cached_at);

  // One growth factor drives every per-row projection in the breakdown, so a
  // pane row and the stat cell above it can never tell different stories.
  const projection = paceForecast((daily?.daily || []).map(Number), daily?.days_in_month || 0, basis);
  const projecting = !!daily && isCurrentMonth(period) && projection.daysRemaining > 0;

  const quick = recentPeriods(QUICK_PERIODS);
  const segPeriods = quick.includes(period) ? quick : [period, ...quick.slice(0, QUICK_PERIODS - 1)];
  const archive = recentPeriods(ARCHIVE_PERIODS);

  const scope = `${tagFilter ? `${tagKey} = ${tagValue} · ` : ""}${accounts.length} ${
    accounts.length === 1 ? "account" : "accounts"
  } · ${periodLabelLong(period)}${
    compare
      ? accountsComparison?.mode === "same_point"
        ? ` vs ${comparisonLabel(accountsComparison, prevPeriod)}`
        : ` vs ${periodLabelLong(prevPeriod)}`
      : " · no comparison"
  }`;
  // Tag keys, grouped: the company's own tags first, AWS's generated ones
  // (aws:…) after, and — disabled — tags that exist but cannot be filtered
  // on yet, so the reason is visible here rather than a mystery.
  const loadedKeys = tagKeys && typeof tagKeys === "object" ? tagKeys : null;
  const allKeys = [...new Set([...(loadedKeys?.keys || []), ...(tagKey ? [tagKey] : [])])].sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: "base" })
  );
  const userKeys = allKeys.filter((k) => !k.startsWith("aws:"));
  const awsKeys = allKeys.filter((k) => k.startsWith("aws:"));
  const noDataKeys = (loadedKeys?.noData || []).filter((k) => !k.startsWith("aws:"));
  const inactiveKeys = (loadedKeys?.inactive || []).filter((k) => !k.startsWith("aws:"));

  return (
    <>
      <AppHeader>
        <div className="freshness">
          <div>
            {dataThrough
              ? `Cost Explorer through ${dataThrough}, ${dataThroughTime} ${DISPLAY_TIMEZONE_LABEL}`
              : "Cost Explorer data unavailable"}
          </div>
          <div>
            {cachedTime ? `Cached ${cachedTime} ${DISPLAY_TIMEZONE_LABEL}` : "Not cached"}
            {" · "}
            <span className={nightlyOk === false ? "freshness-warn" : undefined}>
              {nightlyOk === null ? "nightly job unknown" : nightlyOk ? "nightly job OK" : "nightly job stale"}
            </span>
          </div>
        </div>
        <div className="action-group">
          <button type="button" className="primary" onClick={onRefresh} disabled={refreshing}>
            {refreshing ? "Refreshing…" : "Refresh from AWS"}
          </button>
          {/* The backfill depth is an admin-frequency setting, not a primary
              action — it sits behind this overflow toggle rather than beside
              the button it modifies. */}
          <button
            type="button"
            className="ghost action-more"
            aria-label="Refresh options"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((v) => !v)}
          >
            ⋯
          </button>
          {menuOpen && (
            <div className="action-menu">
              <label htmlFor="backfill-months">Months to pull on next refresh</label>
              <select id="backfill-months" value={refreshMonths} onChange={(e) => setRefreshMonths(Number(e.target.value))}>
                {BACKFILL_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      </AppHeader>

      <div className="control-rail">
        <div className="rail-group">
          <span className="rail-label" id="period-label">
            Period
          </span>
          <SegControl
            label="Period"
            options={segPeriods.map((p) => ({ value: p, label: periodLabel(p) }))}
            value={period}
            onChange={(p) => setParam({ period: p })}
          />
          <select
            aria-label="Earlier month"
            value={archive.includes(period) ? period : ""}
            onChange={(e) => setParam({ period: e.target.value })}
          >
            {archive.map((p) => (
              <option key={p} value={p}>
                {periodLabel(p)}
              </option>
            ))}
          </select>
          <span className="rail-label">Compare</span>
          <SegControl
            label="Compare"
            options={[
              { value: true, label: "On" },
              { value: false, label: "Off" },
            ]}
            value={compare}
            onChange={setCompare}
          />
          {projecting && (
            <>
              <span className="rail-label">Forecast on</span>
              <SegControl
                label="Forecast basis"
                options={FORECAST_BASES.map((b) => ({ value: b.value, label: b.label }))}
                value={basis}
                onChange={(v) => setParam({ basis: v === DEFAULT_FORECAST_BASIS ? null : v })}
              />
            </>
          )}
        </div>
        <span className="rail-scope">{scope}</span>
        <div className="rail-group rail-filters">
          <span className="rail-label">Filter by tag</span>
          <select
            className="rail-select"
            aria-label="Filter by tag"
            value={tagKey || ""}
            onFocus={ensureTagKeys}
            onMouseDown={ensureTagKeys}
            onChange={(e) => setParam({ tag: e.target.value || null, tagv: null, service: null })}
          >
            <option value="">All costs</option>
            {tagKeys === "loading" && <option disabled>Loading tags…</option>}
            {loadedKeys && allKeys.length === 0 && <option disabled>No activated cost allocation tags</option>}
            {userKeys.length > 0 && (
              <optgroup label="Your tags">
                {userKeys.map((k) => (
                  <option key={k} value={k}>
                    {k}
                  </option>
                ))}
              </optgroup>
            )}
            {noDataKeys.length > 0 && (
              <optgroup label="Activated, no costs this month yet">
                {noDataKeys.map((k) => (
                  <option key={k} value={k} disabled>
                    {k}
                  </option>
                ))}
              </optgroup>
            )}
            {inactiveKeys.length > 0 && (
              <optgroup label="Not activated in Billing (can't filter yet)">
                {inactiveKeys.map((k) => (
                  <option key={k} value={k} disabled>
                    {k}
                  </option>
                ))}
              </optgroup>
            )}
            {awsKeys.length > 0 && (
              <optgroup label="AWS-generated">
                {awsKeys.map((k) => (
                  <option key={k} value={k}>
                    {k}
                  </option>
                ))}
              </optgroup>
            )}
          </select>
          {tagKey && (
            <select
              className="rail-select"
              aria-label={`${tagKey} value`}
              value={tagValue || ""}
              onChange={(e) => setParam({ tagv: e.target.value || null, service: null })}
            >
              <option value="">Choose {tagKey}…</option>
              {[...new Set([...tagValues, ...(tagValue ? [tagValue] : [])])].map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>

      <div className="spend-page">
        {errorMsg && <div className="error-banner">{errorMsg}</div>}

        {/* A prompt rather than a metric: the stat row below reads the state of
            the month, and a to-do list is a different kind of thing. Hidden
            entirely when there is nothing to act on. */}
        {tagFilter && (
          <div className="tag-banner" role="status">
            <span>
              Showing only costs tagged{" "}
              <strong>
                {tagKey} = {tagValue}
              </strong>
              . Untagged resources, and usage from before the tag was activated, are not included.
            </span>
            <button type="button" className="link" onClick={() => setParam({ tag: null, tagv: null, service: null })}>
              Show all costs
            </button>
          </div>
        )}
        {tagKey && !tagValue && (
          <div className="tag-banner" role="status">
            <span>
              Choose a <strong>{tagKey}</strong> value in the bar above to filter the page.
            </span>
          </div>
        )}

        {!tagFilter && savings?.total_monthly_savings > 0 && (
          <Link to="/savings" className="savings-strip">
            <span className="savings-strip-amount">{money(savings.total_monthly_savings)}/mo</span>
            <span className="savings-strip-text">
              identified across {savings.recommendation_count}{" "}
              {savings.recommendation_count === 1 ? "recommendation" : "recommendations"}
            </span>
            <span className="savings-strip-cta">Review savings →</span>
          </Link>
        )}

        {pageLoading && !daily ? (
          <SkeletonStats count={4} />
        ) : daily ? (
          <StatRow
            period={period}
            prevPeriod={prevPeriod}
            daily={daily}
            basis={basis}
            openNote={note}
            onToggleNote={setNote}
          />
        ) : (
          // Every figure in the stat row and the chart comes from the daily
          // series. With it unavailable they would all read $0, which is worse
          // than saying so.
          <div className="stat-row">
            <div className="stat-cell">
              <div className="stat-kicker">Month to date</div>
              <div className="stat-note">
                Daily cost data is unavailable — deploy the <code>/costs/daily</code> endpoint, or check the API logs.
                The breakdown below still reads from the nightly snapshot.
              </div>
            </div>
          </div>
        )}

        <NoteStrip id="forecast" openNote={note}>
          <p style={{ margin: "0 0 8px" }}>
            <strong>Forecast ({basisLabel(basis)})</strong> is month to date plus the average daily spend over that
            window, multiplied by the days remaining. It is computed here, not returned by AWS, so a one-off spike
            inside the window will overstate it — widen the window to smooth that out, narrow it to react faster to a
            step change.
          </p>
          <p style={{ margin: "0 0 8px" }}>
            <strong>AWS forecast</strong> is Cost Explorer's own model for the rest of the month, added to the days that
            have already settled. It is the number the AWS console shows, so it is the one to reconcile against. AWS
            withholds it when an account has too little billing history, in which case the cell is not shown. A material
            gap between the two is usually a spike inside our averaging window, or a commitment AWS knows about that a
            daily average cannot see.
          </p>
          <p style={{ margin: 0 }}>
            <strong>Per-row projections</strong> in the breakdown scale each row by the same growth factor as the month
            as a whole — "if this account keeps its current share". There is no per-row daily series to model from, so a
            row that only started yesterday will read low.
          </p>
        </NoteStrip>

        {daily && (
          <DailySpendChart
            period={period}
            prevPeriod={prevPeriod}
            compare={compare}
            daily={daily}
            basis={basis}
            hover={hover}
            onHover={setHover}
            openNote={note}
            onToggleNote={setNote}
          />
        )}

        <WhatMoved
          movers={movers}
          prevPeriod={prevPeriod}
          comparison={moversComparison}
          loading={pageLoading}
          selected={{ accountId, service }}
          onSelect={(nextAccount, nextService) => setParam({ account: nextAccount, service: nextService })}
        />

        <Breakdown
          accounts={accounts}
          accountId={accountId}
          onSelectAccount={(id) => setParam({ account: id, service: null })}
          services={services}
          service={service}
          onSelectService={(s) => setParam({ service: s })}
          usageTypes={usageTypes}
          usageLoading={usageLoading}
          usageError={usageError}
          onFetchUsageTypes={() => loadUsageTypes(true)}
          compare={compare}
          cacheInfo={cacheInfo}
          growth={projection.growth}
          projecting={projecting}
          untracked={untracked}
          onExport={onExport}
          exportingPayer={exportingPayer}
          accountsLoading={pageLoading}
          servicesLoading={servicesLoading}
          openNote={note}
          onToggleNote={setNote}
          tagFilter={tagFilter}
        />

        <footer className="spend-footer">
          © {new Date().getFullYear()} Bizmoto. Powered by{" "}
          <a href="https://pepltd.com.au/" target="_blank" rel="noopener noreferrer">
            Peppermint Innovation
          </a>
          .
        </footer>
      </div>
    </>
  );
}
